/**
 * /register?token=... — invited-client activation flow.
 *
 * R1.2 sub-step 4. CRM-012 + ONB-020.
 *
 * Flow:
 *   1. Read invite token from URL query.
 *   2. Fetch invite info (email, first_name, language, agreement_version).
 *   3. Render the Heroic Commitment in the user's invite language.
 *   4. Collect: password + confirm + signed_name + agreement checkbox.
 *   5. POST /auth/register-from-invite with `agreement_text` set to the
 *      exact text rendered (so the signed evidence is reproducible).
 *   6. On success: show a "you're in" panel + link to /login.
 */

import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';

import {
  COMMITMENT_AGREEMENT_TEXT,
  PRIVACY_POLICY_TEXT,
  DPA_TEXT,
} from '@h3eromap/i18n';
import { ApiError } from '@/lib/api-client';
import * as api from '@/lib/api-client';
import { LegalText } from '@/components/legal-text';

interface FormFields {
  password: string;
  passwordConfirm: string;
  signedName: string;
  agreementAccepted: boolean;
  /** GDP-076 — required: data processing consent. */
  consentDataProcessing: boolean;
  /** GDP-076 — optional analytics consent. */
  consentAnalytics: boolean;
}

type LoadState =
  | { kind: 'loading' }
  | { kind: 'invalidToken' }
  | { kind: 'ready'; invite: api.InviteInfo }
  | { kind: 'success' };

export function RegisterPage(): JSX.Element {
  const { t, i18n } = useTranslation();
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';

  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  const [submitError, setSubmitError] = useState<string | null>(null);
  // #2/#8 — the language the page (and the activated account) will use.
  // Seeded from the invite, then overridable via the in-page toggle. The
  // chosen value is persisted to the client's account on submit, so the
  // language they pick here is what their emails + tools use afterward.
  const [chosenLanguage, setChosenLanguage] = useState<'de' | 'en'>('de');

  /* Fetch invite once. */
  useEffect(() => {
    let active = true;
    if (!token) {
      setState({ kind: 'invalidToken' });
      return;
    }
    void (async () => {
      try {
        const invite = await api.getInvite(token);
        if (!active) return;
        // Switch the UI to the invited language so the agreement reads in the
        // same locale the coach picked when sending the invite.
        setChosenLanguage(invite.language);
        if (invite.language !== i18n.language) {
          await i18n.changeLanguage(invite.language);
        }
        setState({ kind: 'ready', invite });
      } catch {
        if (!active) return;
        setState({ kind: 'invalidToken' });
      }
    })();
    return () => {
      active = false;
    };
  }, [token, i18n]);

  // #2 — in-page language toggle. Switches the rendered language AND the
  // language that will be saved to the account on submit. (The invite
  // effect only runs once on mount, so this is not re-overridden.)
  const switchLanguage = (next: 'de' | 'en'): void => {
    setChosenLanguage(next);
    void i18n.changeLanguage(next);
  };

  const ready = state.kind === 'ready' ? state.invite : null;

  const schema = useMemo(
    () =>
      z
        .object({
          password: z
            .string()
            .min(1, t('login.errors.required'))
            .min(8, t('register.errors.passwordTooWeak'))
            .regex(/[A-Z]/, t('register.errors.passwordTooWeak'))
            .regex(/[0-9]/, t('register.errors.passwordTooWeak')),
          passwordConfirm: z.string().min(1, t('login.errors.required')),
          signedName: z.string().trim().min(1, t('register.errors.signedNameRequired')),
          agreementAccepted: z.literal<boolean>(true).refine((v) => v === true, {
            message: t('register.errors.agreementRequired'),
          }),
          consentDataProcessing: z.literal<boolean>(true).refine((v) => v === true, {
            message: t('register.errors.dataProcessingRequired'),
          }),
          consentAnalytics: z.boolean(),
        })
        .refine((data) => data.password === data.passwordConfirm, {
          path: ['passwordConfirm'],
          message: t('register.errors.passwordsDoNotMatch'),
        }),
    [t],
  );

  const form = useForm<FormFields>({
    resolver: zodResolver(schema),
    defaultValues: {
      password: '',
      passwordConfirm: '',
      signedName: '',
      agreementAccepted: false,
      consentDataProcessing: false,
      consentAnalytics: false,
    },
  });
  const {
    register,
    formState: { errors, isSubmitting },
  } = form;

  if (state.kind === 'loading') {
    return (
      <section className="max-w-2xl mx-auto px-s5 py-s8" data-testid="register-loading">
        <p className="text-gray-2">{t('register.loading')}</p>
      </section>
    );
  }

  if (state.kind === 'invalidToken') {
    return (
      <section className="max-w-2xl mx-auto px-s5 py-s8" data-testid="register-invalid-token">
        <h1 className="font-display text-display-lg text-ink">
          {t('register.invalidToken.title')}
        </h1>
        <p className="mt-s3 text-gray-2">{t('register.invalidToken.body')}</p>
      </section>
    );
  }

  if (state.kind === 'success') {
    return (
      <section className="max-w-2xl mx-auto px-s5 py-s8" data-testid="register-success">
        <h1 className="font-display text-display-lg text-ink">
          {t('register.success.title')}
        </h1>
        <p className="mt-s3 text-gray-2">{t('register.success.body')}</p>
        <Link
          to="/login"
          className="btn-primary mt-s5 inline-flex"
          data-testid="register-success-cta"
        >
          {t('register.success.cta')}
        </Link>
      </section>
    );
  }

  if (!ready) {
    return <section />;
  }

  // Follow the in-page toggle (#2) so the agreement + privacy texts render
  // in the language the client actually chose, not just the invited one.
  const language = chosenLanguage;
  const agreementText = COMMITMENT_AGREEMENT_TEXT[language];

  const onSubmit = async (data: FormFields): Promise<void> => {
    setSubmitError(null);
    try {
      await api.registerFromInvite({
        token,
        password: data.password,
        signedName: data.signedName.trim(),
        agreementVersion: ready.agreement_version,
        agreementText,
        // #8 — persist the chosen language to the account so emails + tools
        // follow it from here on.
        language: chosenLanguage,
        consents: [
          { consentType: 'data_processing', granted: data.consentDataProcessing },
          // #3 — the AI-insights + email-reminder consent checkboxes were
          // removed from the client activation page; clients get both by
          // default (the coach's per-coach AI toggle + cost cap still gate AI).
          { consentType: 'email_notifications', granted: true },
          { consentType: 'ai_insights', granted: true },
          { consentType: 'analytics', granted: data.consentAnalytics },
        ],
      });
      setState({ kind: 'success' });
    } catch (cause) {
      if (cause instanceof ApiError) {
        if (cause.status === 401) {
          setSubmitError(t('register.errors.tokenInvalid'));
        } else if (cause.status === 0) {
          setSubmitError(t('login.errors.networkError'));
        } else {
          setSubmitError(cause.envelope.message || t('register.errors.generic'));
        }
      } else {
        setSubmitError(t('register.errors.generic'));
      }
    }
  };

  return (
    <section className="max-w-2xl mx-auto px-s5 py-s8">
      <header className="mb-s6">
        <div className="flex items-start justify-between gap-s4">
          <h1 className="font-display text-display-lg text-ink">{t('register.title')}</h1>
          {/* #2 — language toggle. Seeded from the invite, switches the page
              and the language the account will be activated in. */}
          <div
            className="flex shrink-0 rounded-md border border-bone-3 overflow-hidden"
            role="group"
            aria-label={t('common.labels.language')}
            data-testid="register-language-toggle"
          >
            {(['de', 'en'] as const).map((code) => (
              <button
                key={code}
                type="button"
                onClick={() => switchLanguage(code)}
                aria-pressed={language === code}
                data-testid={`register-language-${code}`}
                className={`px-s3 py-s1 text-sm font-medium transition-colors ${
                  language === code
                    ? 'bg-flame text-bone'
                    : 'bg-bone-2 text-gray-2 hover:text-ink'
                }`}
              >
                {t(`common.languages.${code}`)}
              </button>
            ))}
          </div>
        </div>
        <p className="mt-s3 text-gray-2">{t('register.subtitle')}</p>
      </header>

      <form
        onSubmit={(event) => {
          void form.handleSubmit(onSubmit)(event);
        }}
        noValidate
        className="space-y-s5"
      >
        <div>
          <label htmlFor="email" className="form-label">
            {t('register.fields.email')}
          </label>
          <input
            id="email"
            type="email"
            value={ready.email}
            disabled
            readOnly
            className="input-field bg-bone-2 text-gray-2"
          />
        </div>

        <div>
          <label htmlFor="password" className="form-label">
            {t('register.fields.password')}
          </label>
          <input
            id="password"
            type="password"
            autoComplete="new-password"
            placeholder={t('register.placeholders.password')}
            className={`input-field ${errors.password ? 'input-error' : ''}`}
            {...register('password')}
          />
          {errors.password?.message && (
            <p className="form-error">{errors.password.message}</p>
          )}
        </div>

        <div>
          <label htmlFor="passwordConfirm" className="form-label">
            {t('register.fields.passwordConfirm')}
          </label>
          <input
            id="passwordConfirm"
            type="password"
            autoComplete="new-password"
            placeholder={t('register.placeholders.passwordConfirm')}
            className={`input-field ${errors.passwordConfirm ? 'input-error' : ''}`}
            {...register('passwordConfirm')}
          />
          {errors.passwordConfirm?.message && (
            <p className="form-error">{errors.passwordConfirm.message}</p>
          )}
        </div>

        <section
          aria-labelledby="agreement-heading"
          className="rounded-md border border-bone-3 bg-bone-2 p-s5"
        >
          <header className="flex items-baseline justify-between">
            <h2
              id="agreement-heading"
              className="font-display text-display-sm text-ink"
            >
              {t('register.agreement.label')}
            </h2>
            <span className="font-mono text-xs text-gray-1">
              {t('register.agreement.version', { version: ready.agreement_version })}
            </span>
          </header>
          <LegalText
            text={agreementText}
            className="mt-s4 max-h-96 overflow-y-auto pr-s3"
          />
        </section>

        <div>
          <label htmlFor="signedName" className="form-label">
            {t('register.fields.signedName')}
          </label>
          <input
            id="signedName"
            type="text"
            placeholder={t('register.placeholders.signedName')}
            className={`input-field ${errors.signedName ? 'input-error' : ''}`}
            {...register('signedName')}
          />
          {errors.signedName?.message && (
            <p className="form-error">{errors.signedName.message}</p>
          )}
        </div>

        <div>
          <label className="flex items-start gap-s3 cursor-pointer">
            <input
              type="checkbox"
              className="mt-1 h-4 w-4 rounded border-bone-3 text-flame focus:ring-flame"
              {...register('agreementAccepted')}
            />
            <span className="text-sm text-gray-2">
              {t('register.agreement.agree')}
            </span>
          </label>
          {errors.agreementAccepted?.message && (
            <p className="form-error">{errors.agreementAccepted.message}</p>
          )}
        </div>

        <PrivacyAndConsentSection
          language={language}
          register={register}
          errors={errors}
        />

        {submitError && (
          <p role="alert" className="form-error" data-testid="submit-error">
            {submitError}
          </p>
        )}

        <button
          type="submit"
          className="btn-primary w-full"
          disabled={isSubmitting}
        >
          {isSubmitting ? t('register.submitting') : t('register.submit')}
        </button>
      </form>
    </section>
  );
}

/* ─────────────────────────────  Privacy & Consent (GDP-076 + GDP-079)  ───────────────────────────── */

interface PrivacyAndConsentProps {
  language: 'de' | 'en';
  register: ReturnType<typeof useForm<FormFields>>['register'];
  errors: ReturnType<typeof useForm<FormFields>>['formState']['errors'];
}

function PrivacyAndConsentSection(props: PrivacyAndConsentProps): JSX.Element {
  const { t } = useTranslation();
  const [openModal, setOpenModal] = useState<'privacy' | 'dpa' | null>(null);

  return (
    <section
      aria-labelledby="privacy-heading"
      className="rounded-md border border-bone-3 bg-bone-2 p-s5"
      data-testid="register-privacy-section"
    >
      <header>
        <h2
          id="privacy-heading"
          className="font-display text-display-sm text-ink"
        >
          {t('register.privacy.label')}
        </h2>
        <p className="mt-s2 text-sm text-gray-2">
          {t('register.privacy.body')}
        </p>
      </header>

      <div className="mt-s3 flex flex-wrap gap-s2">
        <button
          type="button"
          className="text-sm text-flame-ember underline"
          onClick={() => setOpenModal('privacy')}
          data-testid="open-privacy-policy"
        >
          {t('register.privacy.viewPrivacyPolicy')}
        </button>
        <span className="text-gray-1">·</span>
        <button
          type="button"
          className="text-sm text-flame-ember underline"
          onClick={() => setOpenModal('dpa')}
          data-testid="open-dpa"
        >
          {t('register.privacy.viewDpa')}
        </button>
      </div>

      <fieldset className="mt-s4 space-y-s3">
        <legend className="sr-only">{t('register.privacy.consentsLegend')}</legend>

        <ConsentCheckbox
          name="consentDataProcessing"
          register={props.register}
          required
          label={t('register.privacy.consents.dataProcessing.label')}
          help={t('register.privacy.consents.dataProcessing.help')}
          error={props.errors.consentDataProcessing?.message}
          testId="consent-data-processing"
        />
        {/* #3 — the "AI insights" + "email reminders" consent checkboxes are
            intentionally not shown to clients; they are granted by default at
            registration (see onSubmit). */}
        <ConsentCheckbox
          name="consentAnalytics"
          register={props.register}
          label={t('register.privacy.consents.analytics.label')}
          help={t('register.privacy.consents.analytics.help')}
          testId="consent-analytics"
        />
      </fieldset>

      {openModal && (
        <LegalModal
          title={
            openModal === 'privacy'
              ? t('register.privacy.privacyPolicyTitle')
              : t('register.privacy.dpaTitle')
          }
          text={
            openModal === 'privacy'
              ? PRIVACY_POLICY_TEXT[props.language]
              : DPA_TEXT[props.language]
          }
          onClose={() => setOpenModal(null)}
        />
      )}
    </section>
  );
}

interface ConsentCheckboxProps {
  name: keyof FormFields;
  register: ReturnType<typeof useForm<FormFields>>['register'];
  label: string;
  help: string;
  required?: boolean | undefined;
  error?: string | undefined;
  testId?: string | undefined;
}

function ConsentCheckbox(props: ConsentCheckboxProps): JSX.Element {
  return (
    <div>
      <label className="flex items-start gap-s3 cursor-pointer">
        <input
          type="checkbox"
          className="mt-1 h-4 w-4 rounded border-bone-3 text-flame focus:ring-flame"
          data-testid={props.testId}
          {...props.register(props.name)}
        />
        <span className="text-sm text-gray-2">
          <span className="text-ink font-medium">{props.label}</span>
          {props.required && (
            <span className="ml-1 text-flame-ember" aria-label="required">
              *
            </span>
          )}
          <br />
          <span className="text-xs text-gray-1">{props.help}</span>
        </span>
      </label>
      {props.error && <p className="form-error">{props.error}</p>}
    </div>
  );
}

interface LegalModalProps {
  title: string;
  text: string;
  onClose: () => void;
}

function LegalModal(props: LegalModalProps): JSX.Element {
  const { t } = useTranslation();

  // Lock body scroll while the modal is open so the page behind the
  // backdrop doesn't move when the user scrolls inside the modal.
  // Without this the form underneath drifts and the visual layering
  // looks broken (user-reported on the German privacy + DPA modals,
  // 2026-05-17).
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  // Close on Escape — matches dialog norms.
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') props.onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [props]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="legal-modal-title"
      className="fixed inset-0 z-50 flex items-center justify-center p-s4 sm:p-s6"
      onClick={props.onClose}
    >
      {/*
        Backdrop — a separate fully-opaque layer so the underlying form
        is fully obscured. Prior versions used `bg-ink/80` etc. on the
        same element as the modal flex container — at 80% opacity the
        warm bone-colored form behind still bleeds through as a faint
        second copy of the page. A solid `bg-ink` element + a heavier
        backdrop-blur on top removes ALL of that visual noise so the
        legal text reads as a self-contained document, not as floating
        text layered on top of the register form.
      */}
      <div
        className="absolute inset-0 bg-ink backdrop-blur-md"
        aria-hidden="true"
      />

      {/*
        Modal card — relative so it sits above the backdrop. `bg-bone`
        is the H³eroMap parchment surface (vs the page's slightly
        warmer `bg-bone-1`) so the card visibly lifts off even on
        light pages. `ring-1 ring-bone-3` gives a hairline edge that
        keeps the warm color from washing into the dark backdrop;
        `shadow-2xl` raises it to the "important document" elevation
        tier (the form below uses `shadow-sm`/`shadow-xl`); `overflow-
        hidden` clips the rounded corners so the sticky header doesn't
        spill past them.
      */}
      <div
        className="relative bg-bone rounded-2xl shadow-2xl ring-1 ring-bone-3 max-w-3xl w-full max-h-[88vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between gap-s4 border-b border-bone-3 px-s6 py-s4 bg-bone sticky top-0 z-10">
          <h3
            id="legal-modal-title"
            className="font-display text-display-md text-ink"
          >
            {props.title}
          </h3>
          <button
            type="button"
            onClick={props.onClose}
            className="shrink-0 inline-flex items-center justify-center rounded-md px-s3 py-s2 text-sm text-gray-2 hover:text-ink hover:bg-bone-2 focus:outline-none focus:ring-2 focus:ring-flame focus:ring-offset-2 focus:ring-offset-bone transition-colors"
            data-testid="close-legal-modal"
            aria-label={t('common.actions.cancel')}
          >
            {t('common.actions.cancel')}
          </button>
        </header>
        <LegalText
          text={props.text}
          className="px-s6 py-s5 overflow-y-auto flex-1"
        />
      </div>
    </div>
  );
}
